import { useState, useEffect, useMemo } from 'react';
import { Clock, Lock } from 'lucide-react-native';
import { StyleSheet } from 'react-native-unistyles';
import { Box, Text } from '@/components/ui/primitives';
import { Card, CardContent } from '@/components/ui/Card';
import { useTheme } from '@/lib/styling';
import { colors } from '@/lib/theme';
import type { Theme } from '@/theme';
import { formatTimeRemaining, getCurrentTime } from '@/utils/timeUtils';

export interface WeekStatusGameGroup {
  game: { gameTime: string | Date };
}

export interface WeekStatusBannerProps {
  sortedGames: WeekStatusGameGroup[];
  isComplete?: boolean;
}

export default function WeekStatusBanner({ isComplete = false, sortedGames }: WeekStatusBannerProps) {
  const theme = useTheme<Theme>();
  const [timeRemaining, setTimeRemaining] = useState<string>('');
  const [isDeadlinePassed, setIsDeadlinePassed] = useState(false);

  const firstGameTime = useMemo(() => {
    if (!sortedGames.length) {
      return null;
    }
    return new Date(
      Math.min(
        ...sortedGames.map((group) => new Date(group.game.gameTime).getTime()),
      ),
    );
  }, [sortedGames]);

  useEffect(() => {
    if (!firstGameTime) return;

    const updateCountdown = () => {
      const { text, isPassed } = formatTimeRemaining(firstGameTime, getCurrentTime());
      setTimeRemaining(text);
      setIsDeadlinePassed(isPassed);
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000 * 60);

    return () => clearInterval(interval);
  }, [firstGameTime]);

  // If some games have already started (locked), we might not want to show this specific "all games haven't started" banner.
  // Actually, the original logic was `lockedGames === 0`.
  const currentTime = getCurrentTime();
  const lockedGames = sortedGames.filter((group) => new Date(group.game.gameTime) <= currentTime).length;

  if (isComplete || lockedGames > 0 || !firstGameTime) return null;

  return (
    <Card
      marginBottom="s"
      style={[styles.banner, isDeadlinePassed ? styles.deadlinePassed : styles.deadlineOpen]}
    >
      <CardContent style={styles.content}>
        <Box flexDirection="row" alignItems="center" gap="s" paddingHorizontal="none" paddingVertical="s">
          {isDeadlinePassed ? (
            <Lock size={16} color={theme.colors.press} />
          ) : (
            <Clock size={18} color={colors.warning[300]} strokeWidth={2.75} />
          )}
          <Box flex={1} justifyContent="center">
            <Text
              variant="bodySmall"
              color="press"
            >
              {isDeadlinePassed
                ? 'Picks are now locked - games have started'
                : timeRemaining
                  ? `Picks due in ${timeRemaining}`
                  : 'Calculating time remaining...'}
            </Text>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

const styles = StyleSheet.create(theme => ({
  banner: {
    borderWidth: 2,
    elevation: 0,
    shadowColor: 'transparent',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0,
    shadowRadius: 0,
  },
  content: {
    paddingVertical: 0,
  },
  deadlineOpen: {
    backgroundColor: colors.warning[50],
    borderColor: theme.colors.press,
  },
  deadlinePassed: {
    backgroundColor: colors.warning[50],
    borderColor: theme.colors.press,
  },
}));
